import { type AppDescriptor } from '@wix/document-services-types';
import {
  DEFAULT_ACTIONS_ICONS,
  DEFAULT_ACTIONS,
  HELP_ARTICLE_IDS,
} from '../constants/constants';
import { AppManagerApiDependencies } from '../../../types/appManager';
import { TFunction } from '../../useTranslation';
import { TRANSLATIONS } from '../constants/translations';
import { CustomActionConfig } from '@wix/editor-platform-sdk-types';

const EDITOR_ACTION = 'editorActions';

export const actionsMapper = (
  appDefId: string,
  deps: AppManagerApiDependencies,
  actionId: string,
) => {
  switch (actionId) {
    case DEFAULT_ACTIONS.MANAGE_PAGES:
      return deps.openPagesPanel(appDefId);
    case DEFAULT_ACTIONS.DISCOVER_WIDGETS:
      return deps.openAddPanel(appDefId);
    case DEFAULT_ACTIONS.OPEN_DASHBOARD:
      return deps.openDashboardPanel(appDefId);
    default:
      return;
  }
};

export const DEFAULT_APP_DESCRIPTOR: AppDescriptor = {
  mainActions: [],
  customActions: [],
  defaultActions: {
    learnMoreKB: HELP_ARTICLE_IDS.MY_BUSINESS,
  },
};

const getUpgradeInfo = (
  t: (key: string) => string,
): AppDescriptor['defaultActions']['upgrade'] => {
  return {
    upgradeType: 'APP_UPGRADE',
    upgradeText:
      t(TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.UPGRADE.UPGRADE_TEXT) + ' ',
    upgradeLinkText: t(
      TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.UPGRADE.UPGRADE_LINK_TEXT,
    ),
  };
};

const getOpenDashboardAction = (t: TFunction): CustomActionConfig => {
  return {
    title: t(TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.OPEN_DASHBOARD),
    actionId: DEFAULT_ACTIONS.OPEN_DASHBOARD,
    icon: DEFAULT_ACTIONS_ICONS.OPEN_DASHBOARD,
    type: EDITOR_ACTION,
  };
};

const getDiscoverWidgetsAction = (t: TFunction): CustomActionConfig => {
  return {
    title: t(TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.DISCOVER_WIDGETS),
    actionId: DEFAULT_ACTIONS.DISCOVER_WIDGETS,
    icon: DEFAULT_ACTIONS_ICONS.DISCOVER_WIDGETS,
    type: EDITOR_ACTION,
  };
};

const getManagePagesAction = (t: TFunction): CustomActionConfig => {
  return {
    title: t(TRANSLATIONS.MY_BUSINESS.DEFAULT_ACTIONS.MANAGE_PAGES),
    actionId: DEFAULT_ACTIONS.MANAGE_PAGES,
    icon: DEFAULT_ACTIONS_ICONS.MANAGE_PAGES,
    type: EDITOR_ACTION,
  };
};

const deepCloneWithoutFunctions = (obj: Object) =>
  JSON.parse(JSON.stringify(obj));

export const getDefaultAppDescriptor = async (
  appDefId: string,
  deps: AppManagerApiDependencies,
  t: (key: string) => string,
) => {
  let appDescriptor = deps.getAppDescriptor(appDefId) as AppDescriptor;
  if (!appDescriptor) {
    appDescriptor = deepCloneWithoutFunctions(DEFAULT_APP_DESCRIPTOR);

    if (deps.appHasPremiumPlan && deps.appHasPremiumPlan(appDefId)) {
      appDescriptor.defaultActions.upgrade = getUpgradeInfo(t);
    } else {
      const applicationId = deps.getAppDataByAppDefId(appDefId)?.applicationId;
      if (
        applicationId &&
        deps.appHasPremiumOffering &&
        deps.appHasPremiumOffering(applicationId)
      ) {
        appDescriptor.defaultActions.upgrade = getUpgradeInfo(t);
      }
    }

    const appHasDashboard = deps.appHasDashboard(appDefId);
    if (appHasDashboard) {
      appDescriptor.customActions.push(getOpenDashboardAction(t));
    }

    const appHasWidgets = await deps.appHasWidgets(appDefId);
    if (appHasWidgets) {
      appDescriptor.customActions.push(getDiscoverWidgetsAction(t));
    }

    const appHasPages = await deps.appHasPages(appDefId);
    if (appHasPages) {
      appDescriptor.customActions.push(getManagePagesAction(t));
    }
  }
  return appDescriptor;
};
